import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  VStack,
  HStack,
  Checkbox,
  Text,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import { WarningIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';
import { parseDateString, formatDateToString } from 'utils/helpers';
import { useAnswerCodes, useValidationSchema } from '../hooks';
import { ValidatedField } from '../components';
import type { OilTank } from 'api';
import type { HeatingSource } from '../hooks/useAnswerCodes';

const MAX_OIL_TANKS = 3;

interface OilTanksModalProps {
  isOpen: boolean;
  onClose: () => void;
  heatingSource: HeatingSource;
  oilTanks: OilTank[];
  onSave: (tanks: OilTank[]) => void;
  isUpdating?: boolean;
}

const createEmptyOilTank = (id: number, associationCode: string): OilTank => ({
  id,
  construction_type: '',
  other_construction_type: '',
  location: '',
  heating_associated_with: associationCode,
  make: '',
  model_year: null,
  installation_date: null,
  last_inspection_date: null,
  approved_by_ulc_csa_or_wh: false,
  professionally_installed: false,
  any_damage: false,
});

const OilTanksModal = ({
  isOpen,
  onClose,
  heatingSource,
  oilTanks,
  onSave,
  isUpdating,
}: OilTanksModalProps) => {
  const { getOptions, getLabel, getCodeByLabel } = useAnswerCodes();
  const { validateEntity } = useValidationSchema();
  const [localTanks, setLocalTanks] = useState<OilTank[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const associationCode = useMemo(() => {
    const label = heatingSource === 'primary' ? 'Primary' : 'Auxiliary';
    return getCodeByLabel(label, 'oil_storage_tank_heating_associated_with');
  }, [heatingSource, getCodeByLabel]);

  const otherAssociationCode = useMemo(() => {
    const label = heatingSource === 'primary' ? 'Auxiliary' : 'Primary';
    return getCodeByLabel(label, 'oil_storage_tank_heating_associated_with');
  }, [heatingSource, getCodeByLabel]);

  const tanksForThisSource = useMemo(() => {
    return oilTanks.filter(
      (t) => t.heating_associated_with === associationCode,
    );
  }, [oilTanks, associationCode]);

  const tanksForOtherSource = useMemo(() => {
    return oilTanks.filter(
      (t) => t.heating_associated_with === otherAssociationCode,
    );
  }, [oilTanks, otherAssociationCode]);

  const effectiveTotalTanks = tanksForOtherSource.length + localTanks.length;
  const canAddMore = effectiveTotalTanks < MAX_OIL_TANKS;

  useEffect(() => {
    if (isOpen) {
      setLocalTanks(tanksForThisSource.map((t) => ({ ...t })));
      setActiveTabIndex(0);
    }
  }, [isOpen, tanksForThisSource]);

  const getNextId = (): number => {
    const allIds = [...oilTanks, ...localTanks].map((t) => t.id);
    return allIds.length > 0 ? Math.max(...allIds) + 1 : 1;
  };

  const isOtherValue = (code: string, field: string): boolean => {
    if (!code) return false;
    const label = getLabel(code, field as Parameters<typeof getLabel>[1]);
    return label.toLowerCase() === 'other';
  };

  const isTankIncomplete = (tank: OilTank): boolean => {
    return (
      validateEntity(
        'location/oil_tank',
        tank as unknown as Record<string, unknown>,
      ).length > 0
    );
  };

  const handleAddTank = () => {
    if (!canAddMore) return;
    const newTank = createEmptyOilTank(getNextId(), associationCode);
    setLocalTanks((prev) => [...prev, newTank]);
    setActiveTabIndex(localTanks.length);
  };

  const handleRemoveTank = (tankId: number) => {
    setLocalTanks((prev) => prev.filter((t) => t.id !== tankId));
    setActiveTabIndex((prev) => Math.max(0, prev - 1));
  };

  const handleChange = (
    tankId: number,
    field: keyof OilTank,
    value: unknown,
  ) => {
    setLocalTanks((prev) =>
      prev.map((tank) =>
        tank.id === tankId ? { ...tank, [field]: value } : tank,
      ),
    );
  };

  const handleSelectChange =
    (tankId: number, field: keyof OilTank, otherField?: keyof OilTank) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      handleChange(tankId, field, e.target.value);
      if (
        otherField &&
        !isOtherValue(e.target.value, `oil_storage_tank_${field}`)
      ) {
        handleChange(tankId, otherField, '');
      }
    };

  const handleTextChange =
    (tankId: number, field: keyof OilTank) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(tankId, field, e.target.value);
    };

  const handleNumberChange =
    (tankId: number, field: keyof OilTank) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      handleChange(tankId, field, isNaN(value as number) ? null : value);
    };

  const handleDateChange =
    (tankId: number, field: keyof OilTank) => (date: Date | null) => {
      handleChange(tankId, field, formatDateToString(date));
    };

  const handleCheckboxChange =
    (tankId: number, field: keyof OilTank) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(tankId, field, e.target.checked);
    };

  const handleSave = () => {
    const updatedTanks = [...tanksForOtherSource, ...localTanks];
    onSave(updatedTanks);
    onClose();
  };

  const title =
    heatingSource === 'primary'
      ? 'Primary Heating Oil Tanks'
      : 'Auxiliary Heating Oil Tanks';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {localTanks.length === 0 ? (
            <VStack spacing={4} py={8}>
              <Text color="gray.500">No oil tanks added</Text>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="brand"
                variant="outline"
                onClick={handleAddTank}
                isDisabled={!canAddMore || isUpdating}
              >
                Add Oil Tank
              </Button>
              {!canAddMore && (
                <Text fontSize="sm" color="gray.500">
                  Maximum of {MAX_OIL_TANKS} oil tanks total reached
                </Text>
              )}
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  {localTanks.length} tank{localTanks.length !== 1 ? 's' : ''}{' '}
                  for {heatingSource} heating ({effectiveTotalTanks}/
                  {MAX_OIL_TANKS} total)
                </Text>
                <Button
                  leftIcon={<AddIcon />}
                  size="sm"
                  variant="outline"
                  onClick={handleAddTank}
                  isDisabled={!canAddMore || isUpdating}
                >
                  Add Tank
                </Button>
              </HStack>

              <Tabs
                size="sm"
                variant="enclosed"
                index={activeTabIndex}
                onChange={setActiveTabIndex}
              >
                <TabList>
                  {localTanks.map((tank, index) => (
                    <Tab key={tank.id}>
                      <HStack spacing={1}>
                        <Text>Tank {index + 1}</Text>
                        {isTankIncomplete(tank) && (
                          <Icon as={WarningIcon} color="red.400" boxSize={3} />
                        )}
                      </HStack>
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {localTanks.map((tank) => {
                    const showOtherConstruction = isOtherValue(
                      tank.construction_type,
                      'oil_storage_tank_construction_type',
                    );

                    return (
                      <TabPanel key={tank.id} px={0} pt={4}>
                        <VStack spacing={4} align="stretch">
                          <HStack justify="flex-end">
                            <Button
                              leftIcon={<DeleteIcon />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleRemoveTank(tank.id)}
                              isDisabled={isUpdating}
                            >
                              Remove Tank
                            </Button>
                          </HStack>

                          <SimpleGrid columns={2} spacing={4}>
                            <ValidatedField
                              itemPath="location/oil_tank"
                              fieldPath="construction_type"
                              label="Construction Type"
                              entity={
                                tank as unknown as Record<string, unknown>
                              }
                              isDisabled={isUpdating}
                            >
                              <Select
                                variant="main"
                                size="sm"
                                value={tank.construction_type || ''}
                                onChange={handleSelectChange(
                                  tank.id,
                                  'construction_type',
                                  'other_construction_type',
                                )}
                                isDisabled={isUpdating}
                              >
                                {getOptions(
                                  'oil_storage_tank_construction_type',
                                ).map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                            </ValidatedField>

                            <ValidatedField
                              itemPath="location/oil_tank"
                              fieldPath="location"
                              label="Location"
                              entity={
                                tank as unknown as Record<string, unknown>
                              }
                              isDisabled={isUpdating}
                            >
                              <Select
                                variant="main"
                                size="sm"
                                value={tank.location || ''}
                                onChange={handleSelectChange(
                                  tank.id,
                                  'location',
                                )}
                                isDisabled={isUpdating}
                              >
                                {getOptions('oil_storage_tank_location').map(
                                  (opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ),
                                )}
                              </Select>
                            </ValidatedField>
                          </SimpleGrid>

                          {showOtherConstruction && (
                            <FormControl>
                              <FormLabel fontSize="sm">
                                Specify Construction Type
                              </FormLabel>
                              <Input
                                variant="main"
                                size="sm"
                                value={tank.other_construction_type || ''}
                                onChange={handleTextChange(
                                  tank.id,
                                  'other_construction_type',
                                )}
                                isDisabled={isUpdating}
                              />
                            </FormControl>
                          )}

                          <SimpleGrid columns={2} spacing={4}>
                            <ValidatedField
                              itemPath="location/oil_tank"
                              fieldPath="make"
                              label="Make"
                              entity={
                                tank as unknown as Record<string, unknown>
                              }
                              isDisabled={isUpdating}
                            >
                              <Input
                                variant="main"
                                size="sm"
                                value={tank.make || ''}
                                onChange={handleTextChange(tank.id, 'make')}
                                isDisabled={isUpdating}
                              />
                            </ValidatedField>

                            <ValidatedField
                              itemPath="location/oil_tank"
                              fieldPath="model_year"
                              label="Model Year"
                              entity={
                                tank as unknown as Record<string, unknown>
                              }
                              isDisabled={isUpdating}
                            >
                              <Input
                                variant="main"
                                size="sm"
                                type="number"
                                min={1900}
                                max={new Date().getFullYear()}
                                value={tank.model_year ?? ''}
                                onChange={handleNumberChange(
                                  tank.id,
                                  'model_year',
                                )}
                                isDisabled={isUpdating}
                              />
                            </ValidatedField>
                          </SimpleGrid>

                          <SimpleGrid columns={2} spacing={4}>
                            <DateField
                              label="Installation Date"
                              required
                              date={parseDateString(tank.installation_date)}
                              onChange={handleDateChange(
                                tank.id,
                                'installation_date',
                              )}
                              isDisabled={isUpdating}
                              maxDate={new Date()}
                              size="sm"
                            />

                            <DateField
                              label="Last Inspection Date"
                              date={parseDateString(tank.last_inspection_date)}
                              onChange={handleDateChange(
                                tank.id,
                                'last_inspection_date',
                              )}
                              isDisabled={isUpdating}
                              maxDate={new Date()}
                              size="sm"
                            />
                          </SimpleGrid>

                          <Box pt={2}>
                            <VStack spacing={2} align="start">
                              <Checkbox
                                isChecked={
                                  tank.approved_by_ulc_csa_or_wh ?? false
                                }
                                onChange={handleCheckboxChange(
                                  tank.id,
                                  'approved_by_ulc_csa_or_wh',
                                )}
                                isDisabled={isUpdating}
                              >
                                ULC/CSA/WH Approved
                              </Checkbox>

                              <Checkbox
                                isChecked={
                                  tank.professionally_installed ?? false
                                }
                                onChange={handleCheckboxChange(
                                  tank.id,
                                  'professionally_installed',
                                )}
                                isDisabled={isUpdating}
                              >
                                Professionally Installed
                              </Checkbox>

                              <Checkbox
                                isChecked={tank.any_damage ?? false}
                                onChange={handleCheckboxChange(
                                  tank.id,
                                  'any_damage',
                                )}
                                isDisabled={isUpdating}
                              >
                                Any Damage
                              </Checkbox>
                            </VStack>
                          </Box>
                        </VStack>
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <SimpleGrid columns={2} spacing={3} w="full">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSave}
              isDisabled={isUpdating}
            >
              Save
            </Button>
          </SimpleGrid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OilTanksModal;
